import { useEffect, useMemo, useRef, useState } from 'react';
import { getTransactionList } from '../utils/transactionDataApi';
import { toDateOnlyString } from '../utils/dateOnly';

const toApiDate = (date) => toDateOnlyString(date);

export const useTransactions = ({
  mode,
  filters,
  currentPage,
  pageSize,
  customers,
  suppliers,
  refreshVersion,
  enabled = true,
}) => {
  const [state, setState] = useState({
    transactions: [],
    totalPages: 1,
    totalCount: 0,
    incomes: 0,
    expenses: 0,
    loading: true,
    error: '',
    requestKey: '',
  });
  const requestSequence = useRef(0);

  const legacyCustomer = useMemo(() => {
    if (!filters.customerId) return '';
    const customer = customers.find((item) => item._id === filters.customerId);
    return customer ? `${customer.firstName}:${customer.lastName}` : '';
  }, [customers, filters.customerId]);

  const legacySupplier = useMemo(() => {
    if (!filters.supplierId) return '';
    return suppliers.find((item) => item._id === filters.supplierId)?.name || '';
  }, [filters.supplierId, suppliers]);

  const params = useMemo(() => {
    const includeAll = pageSize === 'all';
    return {
      customer: legacyCustomer,
      supplier: legacySupplier,
      keyword: filters.keyword.trim(),
      date: toApiDate(filters.date?.endDate),
      pageNum: currentPage,
      pageSize: includeAll ? 500 : pageSize,
      include_all: includeAll,
      customer_id: filters.customerId,
      supplier_id: filters.supplierId,
      transaction_type: filters.transactionType,
    };
  }, [currentPage, filters, legacyCustomer, legacySupplier, pageSize]);
  const requestKey = useMemo(
    () => JSON.stringify([mode, params, refreshVersion]),
    [mode, params, refreshVersion],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    const sequence = ++requestSequence.current;
    setState((current) => ({ ...current, loading: true, error: '' }));

    getTransactionList(mode, params, { signal: controller.signal })
      .then((response) => {
        if (sequence !== requestSequence.current || controller.signal.aborted) return;
        if (!response || response.status >= 400) throw new Error('Unable to load transactions');
        setState({
          transactions: response.data?.transactions || [],
          totalPages: response.data?.totalPage || 1,
          totalCount: response.data?.totalCount || 0,
          incomes: response.data?.incomes || 0,
          expenses: response.data?.expenses || 0,
          loading: false,
          error: '',
          requestKey,
        });
      })
      .catch((error) => {
        if (controller.signal.aborted || error?.code === 'ERR_CANCELED') return;
        if (sequence === requestSequence.current) {
          setState({
            transactions: [],
            totalPages: 1,
            totalCount: 0,
            incomes: 0,
            expenses: 0,
            loading: false,
            error: 'Unable to load transactions',
            requestKey,
          });
        }
      });

    return () => controller.abort();
  }, [enabled, mode, params, refreshVersion, requestKey]);

  const { requestKey: completedRequestKey, ...publicState } = state;
  if (completedRequestKey !== requestKey) {
    return {
      ...publicState,
      transactions: [],
      totalPages: 1,
      totalCount: 0,
      incomes: 0,
      expenses: 0,
      loading: enabled,
      error: '',
      params,
    };
  }

  return { ...publicState, params };
};
